import React, { useState } from 'react';
import { MoonIcon } from '@heroicons/react/20/solid';

import Switch from '../Switch';
import { classnames } from '../../utils';
import styles from './OpeningHoursInput.module.scss';

const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const toMinutes = (time) => {
	const [hours, minutes] = time.split(':').map(Number);
	return hours * 60 + minutes;
};

/**
 * Opening hours editor.
 *
 * Output value format:
 * ```
 * [{ day: 0, open: '09:00', close: '17:00' }, ...]
 * ```
 *
 * Day index is 0-based, starting from Monday.
 *
 * @typedef {object} OpeningHoursDay
 * @property {number} day
 * @property {string} open
 * @property {string} close
 *
 * @param {object} props
 * @param {OpeningHoursDay[]} [props.value]
 * @param {(value: OpeningHoursDay[]) => void} props.onChange
 * @returns
 */
const OpeningHoursInput = ({ value, onChange }) => {
	const [state, setState] = useState(value || []);

	// Validate the state and call onChange
	const commit = (newState = state) => {
		for (const day of newState) {
			if (toMinutes(day.close) < toMinutes(day.open)) {
				day.close = day.open; // close time can't be before open time
			}
			if (toMinutes(day.open) > toMinutes(day.close)) {
				day.open = day.close; // open time can't be after close time
			}
		}

		setState(newState);
		onChange(newState);
	};

	return (
		<div className={styles.container}>
			{days.map((day, index) => {
				const dayState = state?.find((d) => d.day === index);
				const enabled = !!dayState;

				const toggleDay = () => {
					if (dayState) {
						const newState = state.filter((d) => d.day !== index);
						commit(newState);
					} else {
						const newState = [...state, { day: index, open: '09:00', close: '17:00' }];
						commit(newState);
					}
				};

				return (
					<div key={day} className={classnames(styles.day, enabled && styles.enabled)}>
						<Switch checked={enabled} onChange={toggleDay} />
						<div className={styles.label}>{day}</div>
						<div className={styles.content}>
							<div className={styles.inputs}>
								<div className={styles.input}>
									From
									<input
										type="time"
										value={dayState?.open || ''}
										onChange={(e) => {
											const open = e.target.value;
											setState(state.map((d) => (d.day === index ? { ...d, open } : d)));
										}}
										onBlur={() => commit()}
									/>
								</div>
								<div className={styles.input}>
									To
									<input
										type="time"
										value={dayState?.close || ''}
										onChange={(e) => {
											const close = e.target.value;
											setState(state.map((d) => (d.day === index ? { ...d, close } : d)));
										}}
										onBlur={() => commit()}
									/>
								</div>
							</div>
							<div className={styles.closed} aria-hidden={!enabled}>
								<MoonIcon />
								Closed
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default OpeningHoursInput;
